// Identity spine (Phase 0.5-C) — Customer & Unit lookups and the intake
// dual-write. Truck stays the job record; these helpers keep every new job
// linked to a stable Customer and Unit so renames keep history and one VIN
// shows all its visits.
//
// Every function here is non-fatal by design: intake must never fail
// because a spine write failed, so failures log a warning and return nulls —
// the job is still created with its legacy string fields, and the backfill
// (migrate.js) can re-link it later.
import { generateClient } from 'aws-amplify/api';
import { DEFAULT_LOCATION_ID } from '../events/operationEvents';

const client = generateClient();
const SHOP_ID = DEFAULT_LOCATION_ID; // single-tenant today (DGP-ALBANY)

const norm = (s) => String(s || '').trim();
const lower = (s) => norm(s).toLowerCase();
const upper = (s) => norm(s).toUpperCase();

// Lean inline operations — the generated ones drag every field; these fetch
// exactly what linking needs (same pattern as ROPage's custom query).
const CUSTOMERS_BY_SHOP = /* GraphQL */ `
  query CustomersByShop($shopId: String!, $limit: Int, $nextToken: String) {
    customersByShop(shopId: $shopId, limit: $limit, nextToken: $nextToken) {
      items { id name contactPerson phone email customerType }
      nextToken
    }
  }
`;
const UNITS_BY_VIN = /* GraphQL */ `
  query UnitsByVin($vin: String!) {
    unitsByVin(vin: $vin) { items { id shopId customerId vin unitNumber } }
  }
`;
const UNITS_BY_CUSTOMER = /* GraphQL */ `
  query UnitsByCustomer($customerId: ID!, $limit: Int) {
    unitsByCustomer(customerId: $customerId, limit: $limit) {
      items { id shopId customerId vin unitNumber year make model }
    }
  }
`;
const CREATE_CUSTOMER = /* GraphQL */ `
  mutation CreateCustomer($input: CreateCustomerInput!) {
    createCustomer(input: $input) { id name }
  }
`;
const CREATE_UNIT = /* GraphQL */ `
  mutation CreateUnit($input: CreateUnitInput!) {
    createUnit(input: $input) { id }
  }
`;
const GET_CUSTOMER = /* GraphQL */ `
  query GetCustomer($id: ID!) {
    getCustomer(id: $id) {
      id shopId name contactPerson phone email customerType fleetId
      partsHandling paymentTermsDays warranty notes poRequired creditHold creditHoldNote
    }
  }
`;
const UPDATE_CUSTOMER_TERMS = /* GraphQL */ `
  mutation UpdateCustomerTerms($input: UpdateCustomerInput!) {
    updateCustomer(input: $input) { id paymentTermsDays poRequired }
  }
`;
const TRUCKS_BY_UNIT = /* GraphQL */ `
  query TrucksByUnit($unitId: ID!, $limit: Int, $nextToken: String) {
    trucksByUnit(unitId: $unitId, limit: $limit, nextToken: $nextToken) {
      items { id repairOrder status reason startTime completionTime companyName }
      nextToken
    }
  }
`;

async function listAllCustomers(shopId) {
  const out = [];
  let nextToken = null;
  do {
    const res = await client.graphql({
      query: CUSTOMERS_BY_SHOP,
      variables: { shopId, limit: 500, nextToken },
    });
    const conn = res.data.customersByShop;
    out.push(...(conn.items || []));
    nextToken = conn.nextToken;
  } while (nextToken);
  return out;
}

// Find a customer by name (case-insensitive) within the shop.
export async function findCustomerByName(name, shopId = SHOP_ID) {
  const target = lower(name);
  if (!target) return null;
  const all = await listAllCustomers(shopId);
  return all.find(c => lower(c.name) === target) || null;
}

export async function fetchCustomer(id) {
  if (!id) return null;
  try {
    const res = await client.graphql({ query: GET_CUSTOMER, variables: { id } });
    return res.data.getCustomer || null;
  } catch (err) {
    console.warn('[identitySpine] fetchCustomer failed:', err?.errors?.[0]?.message || err.message);
    return null;
  }
}

// Save billing terms on a customer. Narrow on purpose: this writes only the
// fields the terms editor owns, so it can never clobber a name or a phone
// number that somebody edited on another screen between load and save.
export async function saveCustomerTerms(id, { paymentTermsDays, poRequired }) {
  if (!id) return null;
  try {
    const res = await client.graphql({
      query: UPDATE_CUSTOMER_TERMS,
      variables: {
        input: {
          id,
          ...(paymentTermsDays !== undefined ? { paymentTermsDays } : {}),
          ...(poRequired !== undefined ? { poRequired } : {}),
        },
      },
    });
    return res.data.updateCustomer || null;
  } catch (err) {
    console.warn('[identitySpine] saveCustomerTerms failed:', err?.errors?.[0]?.message || err.message);
    return null;
  }
}

// All customers for the shop — the Customers workspace list.
export async function fetchCustomers(shopId = SHOP_ID) {
  try {
    return await listAllCustomers(shopId);
  } catch (err) {
    console.warn('[identitySpine] fetchCustomers failed:', err?.errors?.[0]?.message || err.message);
    return null; // null = lookup failed (callers can fall back), [] = genuinely none
  }
}

// Every past job for a unit — "one VIN shows all its visits".
export async function fetchUnitHistory(unitId) {
  if (!unitId) return [];
  try {
    const out = [];
    let nextToken = null;
    do {
      const res = await client.graphql({
        query: TRUCKS_BY_UNIT,
        variables: { unitId, limit: 100, nextToken },
      });
      const conn = res.data.trucksByUnit;
      out.push(...(conn.items || []));
      nextToken = conn.nextToken;
    } while (nextToken);
    return out.sort((a, b) => (b.startTime || 0) - (a.startTime || 0));
  } catch (err) {
    console.warn('[identitySpine] fetchUnitHistory failed:', err?.errors?.[0]?.message || err.message);
    return [];
  }
}

// The intake dual-write: resolve-or-create the Customer and Unit for a new
// job's form data. Returns { shopId, customerId, unitId } — any of the ids
// may be null when the data is missing or a lookup failed; the caller still
// creates the job either way.
export async function ensureCustomerAndUnit(truckData, shopId = SHOP_ID) {
  const result = { shopId, customerId: null, unitId: null };
  const name = norm(truckData.companyName);
  if (!name) return result;

  try {
    // ── Customer: exact-name match within the shop, else create ──
    let customer = await findCustomerByName(name, shopId);
    if (!customer) {
      const res = await client.graphql({
        query: CREATE_CUSTOMER,
        variables: {
          input: {
            shopId,
            name,
            contactPerson: norm(truckData.contactPerson) || null,
            phone: norm(truckData.phone) || null,
            email: norm(truckData.email) || null,
            customerType: truckData.customerType || null,
            partsHandling: norm(truckData.partsHandling) || null,
            warranty: truckData.warranty ?? null,
          },
        },
      });
      customer = res.data.createCustomer;
    }
    result.customerId = customer.id;

    // ── Unit: VIN is the strong key, else the customer's unit number ──
    const vin = norm(truckData.vin);
    const unitNum = norm(truckData.truckNumber);
    if (!vin && !unitNum) return result;

    let unit = null;
    if (vin) {
      // The index matches exactly, but stored VINs vary in case (backfill
      // kept them as typed) — try the canonical uppercase form and the raw
      // form before concluding the unit is new.
      const candidates = [...new Set([upper(vin), vin])];
      for (const v of candidates) {
        const res = await client.graphql({ query: UNITS_BY_VIN, variables: { vin: v } });
        unit = (res.data.unitsByVin.items || []).find(u => u.shopId === shopId) || null;
        if (unit) break;
      }
    }
    if (!unit && unitNum) {
      const res = await client.graphql({
        query: UNITS_BY_CUSTOMER,
        variables: { customerId: customer.id, limit: 500 },
      });
      unit = (res.data.unitsByCustomer.items || [])
        .find(u => lower(u.unitNumber) === lower(unitNum)) || null;
    }
    if (!unit) {
      const res = await client.graphql({
        query: CREATE_UNIT,
        variables: {
          input: {
            shopId,
            customerId: customer.id,
            vin: vin ? upper(vin) : null,
            unitNumber: unitNum || null,
            year: norm(truckData.year) || null,
            make: norm(truckData.make) || null,
            model: norm(truckData.model) || null,
            mileage: norm(truckData.mileage) || null,
            hours: norm(truckData.hours) || null,
          },
        },
      });
      unit = res.data.createUnit;
    }
    result.unitId = unit.id;
  } catch (err) {
    console.warn('[identitySpine] dual-write failed (job still created):', err?.errors?.[0]?.message || err.message);
  }
  return result;
}
